import {
  FORWARD_BLOCK_REASONS,
  isActiveDraftComplete,
  resolveActiveDraft,
  resolveAnswerLengthState,
  resolveForwardBlockReason,
  resolveForwardSendBlock,
  selectCurrentStep,
  type FinalSendBlock,
  type ForwardBlockReason,
  type ResponderAnswer,
  type ResponderSessionState,
} from '@/shared-responder-v2-core';

export const ANSWER_TOO_LONG_NOTICE = 'answerTooLong';

export type ForwardBlockNotice =
  | ForwardBlockReason
  | typeof ANSWER_TOO_LONG_NOTICE;

export interface ForwardBlockOutcome {
  readonly notice: ForwardBlockNotice;
  readonly jumpToIndex: number | null;
}

export const ANSWER_TOO_LONG_OUTCOME: ForwardBlockOutcome = {
  notice: ANSWER_TOO_LONG_NOTICE,
  jumpToIndex: null,
};

export function isDraftOverAnswerLimit(draft: ResponderAnswer | null): boolean {
  if (draft?.kind !== 'text') {
    return false;
  }

  return [draft.text, draft.transcript ?? ''].some(
    (text) => resolveAnswerLengthState(text).isOverLimit
  );
}

export function isCurrentAnswerOverLimit(
  state: ResponderSessionState
): boolean {
  const step = selectCurrentStep(state);

  if (step === null) {
    return false;
  }

  return (
    isDraftOverAnswerLimit(resolveActiveDraft(state, step.questionId)) &&
    !isActiveDraftComplete(state, step.questionId)
  );
}

export function resolveFinalSendBlockOutcome(
  block: FinalSendBlock
): ForwardBlockOutcome {
  return {
    notice: FORWARD_BLOCK_REASONS.AnswerRequired,
    jumpToIndex: block.firstOwedIndex,
  };
}

export function resolveForwardBlockOutcome(
  state: ResponderSessionState
): ForwardBlockOutcome | null {
  if (isCurrentAnswerOverLimit(state)) {
    return ANSWER_TOO_LONG_OUTCOME;
  }

  const finalSendBlock = resolveForwardSendBlock(state);

  if (finalSendBlock !== null) {
    return resolveFinalSendBlockOutcome(finalSendBlock);
  }

  const reason = resolveForwardBlockReason(state);

  return reason === null ? null : { notice: reason, jumpToIndex: null };
}
